import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import useAxios from "src/core/shared/hooks/useAxios";
import { IUser } from "src/core/users/api/users.api";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import { IList } from "src/core/lists/api/lists.api";
import { IOrder } from "src/core/orders/api/orders.api";
import { CreateProductReplacementParams } from "src/core/products/api/products.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";

export type ImportFileParams = {
  s3Key: string;
  type: "product" | "nutrition" | "price" | "customer" | "user" | "orders" | "order-picks" | "order-predictions" | "order-imports" | "product-approval",
  orderImportSettingId?: number;
  importSettingId?: number;
};
export type GetProcessJobParams = { pagination: PaginatedParams<IProcessJob>; filters: FilterParams<IProcessJob> };
export type BulkFixErrorParams = { processJobId: number; errorIds: number[] };
export type ListProcessJobsParams = { pagination: PaginatedParams<IProcessJob>; filters: FilterParams<IProcessJob> };
export type ListProcessJobErrorsParams = { processJobId: number; pagination: PaginatedParams<IProcessJobError>; filters: FilterParams<IProcessJobError> };
export const useProcessJobsApi: UseApiRequest<ProcessJobScheduleEndPointConfig> = (name, config) => useAxios(requests[ name ], name, config);

type ProcessJobScheduleEndPointConfig = {
  GET_PROCESS_JOBS: EndpointConfig<
    GetProcessJobParams,
    PaginatedData<IProcessJob>
    >;
    GET_PROCESS_JOB: EndpointConfig<
    number,
    IProcessJob
    >;
    GET_PROCESS_JOB_ERRORS: EndpointConfig<
    ListProcessJobErrorsParams,
    PaginatedData<IProcessJob>
    >;
    BULK_IGNORE_PROCESS_JOB_ERROR: EndpointConfig<
    BulkFixErrorParams,
    IProcessJob
    >;
    BULK_RETRY_PROCESS_JOB_ERROR: EndpointConfig<
    BulkFixErrorParams,
    IProcessJob
    >;
    IMPORT_FILE: EndpointConfig<
    ImportFileParams,
    IProcessJob
    >;
    IMPORT_CUSTOMERS: EndpointConfig<
    any,
    IProcessJob[]
    >;
    IMPORT_PRODUCTS: EndpointConfig<
    any,
    IProcessJob[]
    >;

};

const requests: RequestConfig<ProcessJobScheduleEndPointConfig> = {
  GET_PROCESS_JOBS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/process-jobs`,
    method: "GET",
    params
  }),
  GET_PROCESS_JOB: (processJobId: number) => ({
    url: `${process.env.REACT_APP_API_URL}/core/process-jobs/${processJobId}`,
    method: "GET"
  }),

  GET_PROCESS_JOB_ERRORS: ({ processJobId, ...params }: ListProcessJobErrorsParams) => ({
    url: `${process.env.REACT_APP_API_URL}/core/process-jobs/${processJobId}/errors`,
    method: "GET",
    params
  }),

  BULK_IGNORE_PROCESS_JOB_ERROR: ({ processJobId, ...data }: BulkFixErrorParams) => ({
    url: `${process.env.REACT_APP_API_URL}/core/process-jobs/${processJobId}/errors/bulk-ignore`,
    method: "POST",
    data
  }),
  BULK_RETRY_PROCESS_JOB_ERROR: ({ processJobId, ...data }: BulkFixErrorParams) => ({
    url: `${process.env.REACT_APP_API_URL}/core/process-jobs/${processJobId}/errors/bulk-retry`,
    method: "POST",
    data
  }),
  IMPORT_FILE: (data: ImportFileParams) => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/imports/file`,
    method: "POST",
    data
  }),
  IMPORT_CUSTOMERS: () => ({
    url: `${process.env.REACT_APP_API_URL}/core/connector/import-customers`,
    method: "GET"
  }),
  IMPORT_PRODUCTS: () => ({
    url: `${process.env.REACT_APP_API_URL}/core/connector/import-products`,
    method: "GET"
  })
};

export type ProcessJobStatus = "success" | "error" | "part_error" | "queued" | "in_progress" | "initialising";
export type ProcessJobTriggeredType = "system" | "user";
export type ProcessJobName = keyof ProcessJobPayloads;

export interface IProcessJob<T extends ProcessJobName = any> {
  id: number;
  name: ProcessJobName;
  addedAt: Date;
  startedAt?: Date | null;
  initialisedAt?: Date | null;
  finishedAt?: Date;
  status: ProcessJobStatus;
  statusMessage?: string;
  totalUpdated: number | null;
  totalCreated: number | null;
  retries?: number | null;
  autoRetry?: Date | null;
  triggeredByType: ProcessJobTriggeredType;
  triggeredByUserId?: number;
  jobId?: string;
  jobData: ProcessJobPayloads[T];
  errors?: Array<IProcessJobError<ProcessJobName>> | null;
  priority: number;
  triggeredByUser?: IUser;
  order?: IOrder;
  documents?: IDocument[]
}

export interface IProcessJobError<ProcessJobType extends ProcessJobName = ProcessJobName> {
  id: number;
  type: ProcessJobType;
  code: ProcessJobErrorCode;
  processJobId: number;
  message: string | null;
  //@ts-ignore
  data: ProcessJobErrorPayloads[ProcessJobType] | null;
  status: ProcessJobErrorStatus;
}

export type ProcessJobErrorCode =
  | "data_missing" // soft error
  | "settings_validation" //soft error
  | "db_validation" // hard error
  | "data_validation"; // hard error

export type ProcessJobErrorStatus = "error" | "ignored" | "resolved" | "warn";
export interface ProcessJobPayloads {
  "import-customers": Record<string, unknown>;
  "import-branches": { accountNumber: string; id: number };
  "import-bank-holidays": { year?: number };
  "import-products": Record<string, unknown>;
  "import-customer-order-history": { orderNumber: string };
  "submit-pending-orders": Record<string, unknown>;
  "submit-order-place": {
    orderId: number;
  };
  "submit-order-delete": {
    orderId: number;
  };
  "import-file-product": FileImportBody;
  "import-file-nutrition": FileImportBody;
  "import-file-user": FileImportBody;
  "import-file-customer": FileImportBody;
  "import-file-price": FileImportBody;
  "import-file-order": FileImportBody & { type?: OrderImportType };
  "import-file-product-approval": FileImportBody;
  "api-import-product-approval": { externalId: number | string; delete?: boolean; };
  "authorize-off-session-payments": null;
  "standing-order-validation": {
    type: StandingOrderValidationType;
    scheduleId: number;
    listId: number;
    data?: ISchedule | IList;
  };
  "order-export": { s3Keys?: string[] };
  "api-import-customer": { externalId?: number | string };
  "api-import-bulk-order": Record<string, unknown>;
  "api-export-order": { orderId: number };
  "api-import-order": { externalId: number | string };
  metric: { metricId: number };
  "product-replacement": {
    fromProductId: number;
    toProductId: number;
    orderIds: number[];
  };
  "global-product-replacement": CreateProductReplacementParams & TaskRunnerJobData;
  "api-import-prices": FileImportBody;
}

export interface ProcessJobTaskRunner {
  runOn: "lambda" | "fargate";
  memoryMiB?: number;
  cpu?: number;
}

export interface TaskRunnerJobData {
  taskRunner?: ProcessJobTaskRunner;
}

export enum OrderImportType {
  Standard = "orders",
  Pick = "order-picks",
  Prediction = "order-predictions"
}

export interface FileImportBody {
  s3Key: string;
}

export type StandingOrderValidationType = "create-schedule" | "cancel-schedule" | "update-schedule" | "update-list";
export interface ProcessJobPayloadProcessing {
  internalId: number;
}

export interface ProcessJobErrorPayloads {
  "import-customers": Array<{ accountNumber: string; id?: number }>;
  "import-branches": Array<{
    customerId: number;
    branchNumber: string;
    id?: number;
  }>;
  "import-customer-order-history": Array<{
    orderNumber: string;
  }>;
  "import-bank-holidays": Array<{ year?: number }>;
  "submit-pending-orders": Array<{ orderId: number; error: string }>;
  "import-products": Array<{ partNumber: string; id?: number }>;
  "import-file-product": FileImportErrorData[keyof FileImportErrorData];
  "import-file-price": FileImportErrorData[keyof FileImportErrorData];
  "import-file-customer": FileImportErrorData[keyof FileImportErrorData];
  "import-file-user": FileImportErrorData[keyof FileImportErrorData];
  "import-file-order": FileImportErrorData[keyof FileImportErrorData];
  "import-file-order-pick": FileImportErrorData[keyof FileImportErrorData];
  "standing-order-validation": Array<{ scheduleId: number; listId?: number }>;
  "order-export": { orderId: number };
  "authorize-off-session-payments": { orderId: number };
  metric: Array<{ error: string }>;
  "global-product-replacement": {
    orderId?: number;
    listId?: number;
    replacements: Array<{
      fromProductId: number;
      fromPartNumber: string;
      toProductId: number
      toPartNumber: string;
    }>
  };
}

export interface FileImportErrorData {
  product: Array<{ line?: number; partNumber?: string; message: string }>;
  price: Array<{
    line?: number;
    partNumber?: string;
    priceCode?: string;
    tier?: string;
    message: string;
  }>;
  customer: Array<{
    line?: number;
    customerCode?: string;
    branchCode?: string;
    message: string;
  }>;
  user: Array<{
    line?: number;
    email?: string;
    message: string;
  }>;
  order: Array<{ data: Partial<OrderLineJSON>; message: string }>;
}

export interface OrderLineJSON {
  PartNumber: string;
  BranchCode: string;
  Quantity: string;
  Price?: string;
  PONumber: string;
  DeliveryDate: string;
  OrderCustomData?: Array<{ name: string; description: string; value: string }>;
  LineNumber: number;
}

export interface IDocument {
  id: number;
  fileName: string;
  slug: string;
  key: string;
  mimeType: string;
  size: number;
  folderId: number;
  processJobId?: number;
}